import clsx from 'clsx';
import {
  forwardRef,
  Children,
  type ReactNode,
  useMemo,
  cloneElement,
} from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';
import { _useAriaLabelingProps as useAriaLabelingProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { additionalAvatarWrapperCSS } from './AvatarGroup.sty.js';
import { Item } from './Item.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useRovingFocus } from '../../core/hooks/useRovingFocus.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { Menu } from '../../navigation/menu/Menu.js';

/**
 * Accepted properties for AvatarGroup
 * @public
 */
export type AvatarGroupProps = WithChildren &
  AriaLabelingProps &
  StylingProps &
  DataTestId &
  MaskingProps;

const MAX_AVATARGROUP_ITEMS = 5;

/**
 * AvatarGroup component
 */
const RootAvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: AvatarGroupProps = props;
    const ariaLabelingProps = useAriaLabelingProps(remainingProps);
    const { focusProps, focusClassName } = useFocusRing();
    const intl = useIntl();

    const { containerRef: rovingContainerRef } = useRovingFocus<HTMLDivElement>(
      {
        initialIndex: undefined,
        direction: 'horizontal',
        focusableChildrenSelector: '.rovingFocusElement',
      },
    );

    const [displayValues, additionalValues] = useMemo(() => {
      const childrenArray = Children.toArray(children);
      let displayValues: ReactNode[];
      const additionalValues: ReactNode[] = [];

      if (childrenArray.length > MAX_AVATARGROUP_ITEMS) {
        displayValues = childrenArray.splice(0, MAX_AVATARGROUP_ITEMS);
        for (let i = 0; i < childrenArray.length; i++) {
          const child = childrenArray[i];
          if (isSlot(child, Item)) {
            additionalValues.push(
              cloneElement(
                <Item
                  abbreviation={child.props.abbreviation}
                  tooltipText={child.props.tooltipText}
                />,
                {
                  ...child.props,
                  key: i,
                  isCollapsed: true,
                },
              ),
            );
          }
        }
      } else {
        displayValues = childrenArray;
      }

      return [displayValues, additionalValues];
    }, [children]);

    const rootRef = useMergeRefs([forwardedRef, rovingContainerRef]);

    const formatMessageAriaLabel = intl.formatMessage(
      {
        id: '4ER0rfN8lI3HtaFd',
        defaultMessage:
          'Group of Avatars. {hiddenAvatars} hidden, {totalAvatars} total users',
        description: 'Label for the collapsed Avatars.',
      },
      {
        hiddenAvatars: additionalValues.length,
        totalAvatars: additionalValues.length + displayValues.length,
      },
    );

    const ariaLabel = ariaLabelingProps['aria-label'] ?? formatMessageAriaLabel;

    return displayValues.length ? (
      <Flex
        gap={4}
        alignItems="center"
        ref={rootRef}
        {...focusProps}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        aria-label={ariaLabel}
        className={clsx(consumerClassName, focusClassName)}
        style={consumerStyle}
      >
        <Flex alignItems="center" gap={0}>
          {displayValues}
        </Flex>

        {additionalValues.length > 0 && (
          <Menu>
            <Menu.Trigger>
              <Button
                size="condensed"
                variant="emphasized"
                className={clsx(
                  'rovingFocusElement',
                  consumerClassName,
                  additionalAvatarWrapperCSS,
                  focusClassName,
                )}
              >
                +{additionalValues.length}
              </Button>
            </Menu.Trigger>
            <Menu.Content
              data-dtrum-mask={dataDtrumMask}
              data-dtrum-allow={dataDtrumAllow}
            >
              {additionalValues}
            </Menu.Content>
          </Menu>
        )}
      </Flex>
    ) : null;
  },
);

/**
 * The `Avatar` component can be used as a visual representation of multiple users.
 * @public
 */
export const AvatarGroup = Object.assign(RootAvatarGroup, {
  Item,
});
